import { useState, useEffect, useCallback } from 'react';

/**
 * Detect iOS Safari (beforeinstallprompt is not supported).
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detect if app is already installed (standalone mode).
 */
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

/**
 * Custom hook: provides PWA install prompt + helpers.
 * The deferred prompt is captured globally in App.js and stored on window.__pwaPrompt.
 */
export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(false);
  const dismissed = localStorage.getItem('pwa_dismissed') === 'true';
  const installed = isStandalone();
  const ios = isIOS();

  useEffect(() => {
    // On Android/Chrome, check if deferred prompt is available
    const check = () => setCanInstall(!!window.__pwaPrompt);
    check();
    window.addEventListener('pwa-prompt-ready', check);
    return () => window.removeEventListener('pwa-prompt-ready', check);
  }, []);

  const triggerInstall = useCallback(async () => {
    const prompt = window.__pwaPrompt;
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      window.__pwaPrompt = null;
      setCanInstall(false);
    }
    return outcome === 'accepted';
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem('pwa_dismissed', 'true');
  }, []);

  // Should show install UI?
  const shouldShow = !installed && !dismissed;

  return { canInstall, ios, installed, dismissed, shouldShow, triggerInstall, dismiss };
}
